import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { APP_ID } from '@core/hubconfig';
import { ApplicationAccessService } from '@core/services/application-access/application-access.service';
import { FeatureFlagsService } from '@core/services/feature-flags/feature-flags.service';
import { HubDestroyService } from '@core/services/hub-destroy/hub-destroy.service';
import { TenantsStoreService } from '@core/services/tenants/tenants-store.service';
import { filterDefined } from '@core/utils/filter-defined';
import { EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AppState } from '@core/store/application/application.state';
import { ApplicationMode } from '@core/interfaces/application';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { HeaderComponent, OlAppLogoComponent } from '@ol/kit';
import { MainHeaderSearchbarComponent } from '@shared/features/hub-main-header/main-header-searchbar/main-header-searchbar.component';
import { resolvePlatformUrl } from '@core/utils/platform-embed';
import { MainHeaderActionsComponent } from '@shared/features/hub-main-header/main-header-actions/main-header-actions.component';

@Component({
  selector: 'hub-main-header',
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HubDestroyService],
  imports: [
    RouterModule,
    NgbDropdownModule,
    HeaderComponent,
    OlAppLogoComponent,
    MainHeaderSearchbarComponent,
    MainHeaderActionsComponent,
  ],
})
export class MainHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly hubDestroyService = inject(HubDestroyService);
  private readonly tenantsStoreService = inject(TenantsStoreService);
  private readonly store = inject(Store);

  readonly isLicensePortal = signal(false);
  readonly shouldSearchBarBeVisible = signal(true);
  // One id for the header link, the projected kit logo and (via index.html) the
  // favicon — see APP_ID.
  protected readonly appId = APP_ID;
  // Embedded, the shell's own origin — the kit header's OL-Hub brand mark is
  // origin-gated and the static env value points at the deployed platform.
  protected readonly platformUrl = resolvePlatformUrl();
  // The kit header flag-gates any app-switcher entry declaring a `requiredFlag`
  // off this snapshot. No entry declares one today.
  protected readonly flagsSnapshot = inject(FeatureFlagsService).flagsSnapshot;
  // The caller's effective allow set (Layer A ∩ Layer B) — the switcher enables only these.
  private readonly applicationAccessService = inject(ApplicationAccessService);
  protected readonly applicationAccess = this.applicationAccessService.applicationAccess;

  ngOnInit(): void {
    this.applicationAccessService
      .loadMyAccess()
      .pipe(
        this.hubDestroyService.takeUntilDestroy(),
        // Swallow only — the service leaves `loaded: false`, which the switcher reads as "don't gate".
        // Mapping this to an empty allow set would disable every gated app on a transient failure.
        catchError(() => EMPTY),
      )
      .subscribe();

    this.tenantsStoreService.isLicensePortal$
      .pipe(
        filterDefined<boolean>(),
        distinctUntilChanged(),
        this.hubDestroyService.takeUntilDestroy(),
      )
      .subscribe((isLicensePortal) => {
        this.isLicensePortal.set(isLicensePortal);
      });

    const currentMode = this.store.selectSnapshot(AppState.mode);
    if (currentMode === ApplicationMode.hub) {
      this.shouldSearchBarBeVisible.set(this.isSearchBarVisibleForUrl(this.router.url));
      this.router.events.pipe(this.hubDestroyService.takeUntilDestroy()).subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.shouldSearchBarBeVisible.set(this.isSearchBarVisibleForUrl(event.url));
        }
      });
    } else {
      this.shouldSearchBarBeVisible.set(false);
    }
  }

  // Routes that own the full page and hide the header search bar. Matched by
  // exact path segment (not substring) so e.g. `/get-started-guide` wouldn't
  // accidentally hide it.
  private static readonly SEARCHBAR_HIDDEN_ROUTES = ['get-started'];

  private isSearchBarVisibleForUrl(url: string): boolean {
    const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
    return !segments.some((segment) =>
      MainHeaderComponent.SEARCHBAR_HIDDEN_ROUTES.includes(segment),
    );
  }
}
